import {
  CounterConfig,
  CounterState,
  CounterType,
  useIntegrationManagedRoutines,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useController } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { number, string, object as yupObject } from 'yup';
import { useFormContext } from '../../forms';
import { RoutineExecutionForm, RoutineExecutionFormProps } from '../types';
import { CounterExecutionComponent } from './counterExecutionComponent';
import { IntegrationManagedCounterNotice } from './integrationManagedCounterNotice';

const roundDecimals = (value = 0, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.trunc(value * factor) / factor;
};

export const getExecutionFormDefaultValues = (
  _: CounterConfig,
  state: CounterState
) => {
  const previous = state?.state?.count;
  const baseline =
    _?.counterType === CounterType.Simple
      ? 0
      : roundDecimals(previous ?? 0, _?.decimalPlaces ?? 0);
  const counterType = _?.counterType ? _?.counterType : CounterType.Simple;
  return {
    count: undefined,
    userCount: undefined,
    oldValue: baseline,
    counterType,
  } as any;
};

export const getExecutionFormResolver = () => {
  const validationSchema = yupObject({
    count: number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .nullable()
      .notRequired(),
    oldValue: number().notRequired().nullable(),
    userCount: number().notRequired().nullable(),
    counterType: string().notRequired(),
  });
  return yupResolver(validationSchema);
};

export const CounterExecutionForm: RoutineExecutionForm<
  CounterConfig,
  CounterState
> = (props: RoutineExecutionFormProps<CounterConfig, CounterState>) => {
  const formKey = 'count';
  const { control, setValue, getValues } = useFormContext();
  const { field } = useController({
    control: control,
    name: formKey,
  });

  const { integrationManagedRoutines } = useIntegrationManagedRoutines(
    props.workspaceAsset?.id
  );
  const integrationManaged = props.workspaceRoutineId
    ? integrationManagedRoutines.get(props.workspaceRoutineId)
    : undefined;
  const isIntegrationManaged = !!integrationManaged;

  useEffect(() => {
    const existing = props.currentState?.state?.count;
    if (existing !== undefined && existing !== null) {
      const baseline =
        props.config?.counterType === CounterType.Simple
          ? 0
          : roundDecimals(existing, props.config.decimalPlaces ?? 0);
      setValue('oldValue', baseline, {
        shouldDirty: false,
        shouldValidate: false,
      });
    } else {
      // Use the same baseline calculation as defaultValues to avoid marking form as dirty
      const baseline =
        props.config?.counterType === CounterType.Simple
          ? 0
          : roundDecimals(0, props.config.decimalPlaces ?? 0);
      setValue('oldValue', baseline, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
    if (field.value !== undefined && field.value !== null && field.value !== '')
      return;
    if (
      props.isIssueEditRoutine &&
      existing !== undefined &&
      existing !== null
    ) {
      setValue(formKey, existing, {
        shouldDirty: false,
        shouldValidate: false,
      });
    } else {
      setValue(formKey, undefined as any, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
    setValue('userCount', undefined as any, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [props.currentState]);

  // Must stay after the effect above, which blanks the count on its first pass.
  // Baseline matches the reading, so the operator is credited no change.
  useEffect(() => {
    if (!isIntegrationManaged) return;
    const existing = props.currentState?.state?.count;
    if (existing === undefined || existing === null) return;

    const truncNumber = roundDecimals(
      existing,
      props.config.decimalPlaces ?? 0
    );
    setValue(formKey, truncNumber, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue('oldValue', truncNumber, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue('userCount', 0, { shouldDirty: false, shouldValidate: false });
  }, [isIntegrationManaged, props.currentState]);

  const handleChange = (value: number) => {
    if (value === null || Number.isNaN(value)) {
      setValue(formKey, undefined as any, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('userCount', undefined as any, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    const truncNumber = roundDecimals(value, props.config.decimalPlaces ?? 0);
    field.onChange(truncNumber);
    const existing = props.currentState?.state?.count;
    let baseline: number | undefined;
    if (existing !== undefined && existing !== null) {
      baseline =
        props.config?.counterType === CounterType.Simple
          ? 0
          : roundDecimals(existing, props.config.decimalPlaces ?? 0);
    } else {
      const oldVal = getValues('oldValue');
      baseline = oldVal !== undefined ? oldVal : undefined;
    }
    const delta =
      baseline === undefined
        ? truncNumber
        : roundDecimals(
            truncNumber - baseline,
            props.config.decimalPlaces ?? 0
          );
    setValue('userCount', delta, { shouldDirty: true, shouldValidate: true });
  };

  // The SIMPLE zeroing is for manual entry, which starts from scratch each
  // submission; a managed counter shows the reading itself.
  const initialValue = isIntegrationManaged
    ? roundDecimals(
        props.currentState?.state?.count || 0,
        props.config.decimalPlaces ?? 0
      )
    : props.config?.counterType === CounterType.Simple
    ? 0
    : roundDecimals(
        props.currentState?.state.count || 0,
        props.config.decimalPlaces ?? 0
      );

  return (
    <View style={styles.container}>
      {integrationManaged && (
        <IntegrationManagedCounterNotice
          provider={integrationManaged.provider}
        />
      )}
      <CounterExecutionComponent
        onBlur={field.onBlur}
        instructions={props.config.instructions}
        initialValue={initialValue}
        onChange={handleChange}
        value={field.value}
        decimalPlaces={
          props.config.decimal ? props.config.decimalPlaces : undefined
        }
        counterType={props.config.counterType}
        isIssueEditRoutine={props.isIssueEditRoutine}
        disabled={isIntegrationManaged}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 6,
  },
});
